import {
  getAttachmentThumbnailForScratchpad,
  getErrorMessage,
  isResponseError
} from "@coxauto-ui/cxm/communications/api";
import { SendCommunicationAttachmentResponse } from "@coxauto-ui/cxm/communications/interfaces";
import { Spinner } from "@interstate/components/Spinner";
import { XMarkIcon } from "@interstate/components/Icons";
import { useCallback, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { ConversationContext } from "../context";
import { replyAttachmentScratchpadThumbnail } from "../testIds";
import { useToaster } from "@interstate/components/Toast";
import { VideoCameraIcon } from "@interstate/components/Icons";

export interface MessageAttachmentThumbnailProps {
  attachmentMeta: SendCommunicationAttachmentResponse;
  onRemoveThumbnail: (key: string, token: string) => void;
  fileType?: string;
}
const ThumbWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-right: 2%;
  border-radius: 3px;
  border: 0.5px solid var(--surface-muted, #e3e3e3);
  background: #005ba8;

  &:hover {
    opacity: 0.6;
    background: #959595;
    border: solid 1px;
  }
`;
const Thumb = styled.img`
  height: 40px;
  width: 40px;
`;
const CloseThumbIcon = styled(XMarkIcon)`
  position: absolute;
  top: -5px;
  right: -5px;
  cursor: pointer;
  width: 16px;
  height: 16px;
  background: #959595;
  border-radius: 100%;
  opacity: 0.9;
  font-weight: bold;
  border: solid 1px;
`;
const VideoAttachmentPreview = styled.div`
  margin-right: 10px;
  margin-left: 10px;
  margin-bottom: 5px;
  margin-top: 10px;
`;
const VideoIcon = styled(VideoCameraIcon)`
  width: 20px;
  height: 20px;
  background: #005ba8;
  color: #fff;
`;

export function MessageAttachmentThumbnail(
  props: MessageAttachmentThumbnailProps
) {
  const { env } = useContext(ConversationContext);
  const [imageUrl, setImageUrl] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast: toaster } = useToaster();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  useEffect(() => {
    setIsLoading(true);
    getAttachmentThumbnailForScratchpad(
      props.attachmentMeta.key,
      props.attachmentMeta.token,
      env
    ).then(resultBinary => {
      if (isResponseError(resultBinary)) {
        toaster("Error", getErrorMessage(resultBinary), noop, undefined);
      } else {
        const test = resultBinary as ArrayBuffer;
        const url = window.URL.createObjectURL(
          new Blob([new Uint8Array(test).buffer])
        );
        setImageUrl(url);
      }
      setIsLoading(false);
    });
  }, [env, props.attachmentMeta.key, props.attachmentMeta.token]);

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);
  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <ThumbWrapper
      data-testid={`${replyAttachmentScratchpadThumbnail}-${props.attachmentMeta.key}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {(isLoading && <Spinner id="attachment-thumbnail-loading-indicator" />) ||
        (props.fileType === "video" ? (
          <VideoAttachmentPreview>
            <VideoIcon />
          </VideoAttachmentPreview>
        ) : (
          <Thumb
            alt="..."
            src={imageUrl}
            title={props.attachmentMeta.fileName}
          />
        ))}
      {isHovered && (
        <CloseThumbIcon
          onClick={() =>
            props.onRemoveThumbnail(
              props.attachmentMeta.key,
              props.attachmentMeta.token
            )
          }
        />
      )}
    </ThumbWrapper>
  );
}

export default MessageAttachmentThumbnail;
